import React, { useState, useEffect } from 'react'
import { API_URL } from '../config/api'
import { Input, Button, message, Card } from 'antd'
import { UserOutlined, LockOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch } from '../hooks/redux'
import { loginStart, loginSuccess, loginFailure, logout } from '../store/slices/authSlice'
import BitnukLogo from '../components/Common/BitnukLogo'
import { PLATFORM_NAME } from '../config/brand'

const AdminLoginPage: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [submitting, setSubmitting] = useState(false)
  const [checkingSession, setCheckingSession] = useState(true)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  // 校验本地 token，有效管理员才自动跳转，避免过期 token 导致重定向循环
  useEffect(() => {
    let cancelled = false

    const verifySession = async () => {
      const token = localStorage.getItem('token')
      if (!token) {
        if (!cancelled) setCheckingSession(false)
        return
      }

      try {
        const response = await fetch(`${API_URL}/auth/verify`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        if (!response.ok) {
          localStorage.removeItem('token')
          localStorage.removeItem('user')
          dispatch(logout())
          if (!cancelled) setCheckingSession(false)
          return
        }

        const data = await response.json()
        if (
          !cancelled &&
          data.success &&
          data.data?.user?.accountType === 'admin'
        ) {
          navigate('/admin/dashboard', { replace: true })
          return
        }
      } catch {
        // 网络异常时仍展示登录表单
      }

      if (!cancelled) setCheckingSession(false)
    }

    verifySession()
    return () => {
      cancelled = true
    }
  }, [dispatch, navigate])

  const handleLogin = async () => {
    if (!email || !password) {
      message.error('请输入邮箱和密码')
      return
    }

    setSubmitting(true)
    dispatch(loginStart())

    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          isAdminLogin: true, // 管理员登录入口
        }),
      })

      const data = await response.json()

      if (data.success) {
        const userData = {
          id: data.data.user.id,
          email: data.data.user.email,
          name: data.data.user.name,
          kycStatus: data.data.user.kycStatus,
          accountType: data.data.user.accountType,
        }

        dispatch(loginSuccess({ user: userData, token: data.data.token }))
        message.success('登录成功！')

        setTimeout(() => {
          navigate('/admin/dashboard')
        }, 500)
      } else {
        dispatch(loginFailure(data.error?.message || '登录失败'))
        message.error(data.error?.message || '登录失败')
      }
    } catch (error) {
      dispatch(loginFailure('网络错误，请稍后重试'))
      message.error('网络错误，请稍后重试')
    } finally {
      setSubmitting(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleLogin()
    }
  }

  if (checkingSession) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-gray-400 text-sm">正在检查登录状态...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <Card 
        className="w-full max-w-md bg-gray-800 border-gray-700"
        styles={{ body: { padding: '40px' } }}
      >
        {/* Logo 和标题 */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <BitnukLogo height={40} />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">{PLATFORM_NAME} 管理后台</h1>
          <p className="text-gray-400 text-sm">请使用管理员账户登录</p>
        </div>

        {/* 登录表单 */}
        <div className="space-y-6">
          <div>
            <label className="text-gray-300 text-sm font-medium block mb-2">
              管理员邮箱
            </label>
            <Input
              prefix={<UserOutlined className="text-gray-500" />}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="请输入管理员邮箱"
              size="large"
              className="bg-gray-700 border-gray-600 text-white placeholder-gray-500"
            />
          </div>

          <div>
            <label className="text-gray-300 text-sm font-medium block mb-2">
              密码
            </label>
            <Input.Password
              prefix={<LockOutlined className="text-gray-500" />}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="请输入密码"
              size="large"
              className="bg-gray-700 border-gray-600 text-white placeholder-gray-500"
            />
          </div>

          <Button
            type="primary"
            onClick={handleLogin}
            loading={submitting}
            block
            size="large"
            className="h-12 bg-gradient-to-r from-[#f0b90b] to-[#d4a00a] border-none text-black font-semibold hover:from-[#d4a00a] hover:to-[#f0b90b]"
          >
            登录管理后台
          </Button>
        </div>

        {/* 底部链接 */}
        <div className="mt-6 text-center">
          <Button 
            type="link" 
            onClick={() => navigate('/login')}
            className="text-gray-400 hover:text-[#f0b90b]"
          >
            返回用户登录
          </Button>
        </div>

        {/* 版权信息 */}
        <div className="mt-8 text-center text-gray-500 text-xs">
          © 2025 {PLATFORM_NAME}. All rights reserved.
        </div>
      </Card>
    </div>
  )
}

export default AdminLoginPage
